import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js'
import type { Stripe, StripeElements } from '@stripe/stripe-js'
import React from 'react'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import { ActionsContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import type { Option } from '@/app/component/form/types'
import { useSaveCreditCard } from '@/app/module/payments/hooks/use-save-credit-card'

type Props = {
  elements: StripeElements
  countries: Option[]
  stripe: Stripe
  user: { name: string; email: string }
}

const AddCreditCardForm: React.FC<Props> = ({ elements, countries = [], stripe, user }) => {
  const [saveCard, { isLoading }] = useSaveCreditCard()

  const handleSubmit = React.useCallback(
    async (values: Schema) => {
      await saveCard(stripe, elements, {
        name: values.name,
        email: values.email,
        address: {
          line1: values.line1,
          line2: values.line2,
          city: values.city,
          country: values.country,
          postal_code: values.postcode,
        },
      })
    },
    [elements, stripe, saveCard],
  )

  const fields = React.useMemo(
    () =>
      ({
        name: {
          type: 'text',
          fieldProps: { autoFocus: true, label: "Card holder's name", name: 'name' },
        },
        email: {
          type: 'email',
          fieldProps: { label: 'Email', name: 'email' },
        },
        card: {
          type: 'stripe-element',
          fieldProps: {
            name: 'card',
            label: 'Card number',
            component: CardNumberElement,
          },
        },
        expiry: {
          type: 'stripe-element',
          fieldProps: {
            name: 'expiry',
            label: 'Expiry (MM/YY)',
            component: CardExpiryElement,
          },
        },
        cvc: {
          type: 'stripe-element',
          fieldProps: {
            name: 'cvc',
            label: 'CVC (***)',
            component: CardCvcElement,
          },
        },
        line1: {
          type: 'text',
          fieldProps: { label: 'Address 1', name: 'line1' },
        },
        line2: {
          type: 'text',
          fieldProps: { label: 'Address 2', name: 'line2' },
        },
        city: {
          type: 'text',
          fieldProps: { label: 'City', name: 'city' },
        },
        country: {
          type: 'autocomplete',
          fieldProps: { label: 'Country', name: 'country', options: countries },
        },
        postcode: {
          type: 'text',
          fieldProps: { label: 'Zip / Postal code', name: 'postcode' },
        },
      }) as const,
    [countries],
  )

  const defaultValues = React.useMemo(
    () => ({
      name: user?.name || '',
      email: user?.email || '',
      card: { empty: true, complete: false },
      expiry: { empty: true, complete: false },
      cvc: { empty: true, complete: false },
      line1: '',
      line2: '',
      city: '',
      country: '',
      postcode: '',
    }),
    [user?.email, user?.name],
  )

  return (
    <Form
      defaultValues={defaultValues}
      fields={fields}
      name="add-credit-card"
      schema={schema}
      shouldReset
      onSubmit={handleSubmit}
      renderActions={({ isDirty }) => (
        <ActionsContainer>
          <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
            Add Card
          </SubmitButton>
        </ActionsContainer>
      )}
    />
  )
}

const stripeSchema = z.object({
  complete: z.boolean(),
  empty: z.boolean(),
  error: z.string().optional(),
})

const nameRequiredError = "Card holder's name is required"
const emailRequiredError = 'Email is required'
const emailInvalidError = 'Invalid email address'
const line1RequiredError = 'Address 1 is required'
const countryRequiredError = 'Country is required'
const cityRequiredError = 'City is required'
const postcodeRequiredError = 'Zip / Postal code is required'

const schema = z.object({
  name: z.string({ required_error: nameRequiredError }).min(1, nameRequiredError),
  email: z
    .string({
      required_error: emailRequiredError,
    })
    .email(emailInvalidError),
  line1: z
    .string({
      required_error: line1RequiredError,
    })
    .min(1, line1RequiredError),
  line2: z.string().optional(),
  city: z
    .string({
      required_error: cityRequiredError,
    })
    .min(1, cityRequiredError),
  country: z
    .string({
      required_error: countryRequiredError,
    })
    .min(1, countryRequiredError),
  postcode: z
    .string({
      required_error: postcodeRequiredError,
    })
    .min(1, postcodeRequiredError),
  card: stripeSchema.refine(
    (val) => !val.empty && val.complete,
    (val) => ({
      message: getStripFieldError(val, 'Card number'),
    }),
  ),
  expiry: stripeSchema.refine(
    (val) => !val.empty && val.complete,
    (val) => ({
      message: getStripFieldError(val, 'Card expiry'),
    }),
  ),
  cvc: stripeSchema.refine(
    (val) => !val.empty && val.complete,
    (val) => ({
      message: getStripFieldError(val, 'CCV'),
    }),
  ),
})

type StripeSchema = z.infer<typeof stripeSchema>
type Schema = z.infer<typeof schema>

const getStripFieldError = (val: StripeSchema, field: string) => {
  if (val.error) {
    return val.error
  }
  if (val.empty) {
    return `${field} is required`
  }
  if (!val.complete) {
    return `${field} is incomplete`
  }
  return undefined
}

export default AddCreditCardForm
